import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, HeartPulse, LayoutGrid, ListFilter, Users } from 'lucide-react';
import { FlowBoard } from './schedule/FlowBoard';
import { OrGrid } from './schedule/OrGrid';
import { BookCaseDialog } from './schedule/BookCaseDialog';
import { PatientScheduleRoster } from './schedule/PatientScheduleRoster';
import { StaffRosters } from './schedule/StaffRosters';
import { DayBriefPanel } from '@/components/ai/AscAiPanels';
import {
    useAscSchedule, useAscActions, useAscStaffing, localToday, ASC_FACILITY_ID,
    type OrCase, type RoomStay, type Stage,
} from './schedule/useAscSchedule';

// ── ASC Scheduling (Rolls-Royce rework, 2026-08-15) ─────────────────────────
// Two views, one page: the live Patient Flow board (Pre-op → OR → PACU →
// Discharge with real bay occupancy) on top, and the OR schedule grid
// (rooms × time, rich patient cards, edit/reschedule/cancel) below.
// Replaces the generic embed per Wayne's ruling — purpose-built for the
// Santa Fe pilot on the verified booking + room-flow RPC family.

function shiftDay(dateStr: string, delta: number): string {
    const d = new Date(`${dateStr}T12:00:00`);
    d.setDate(d.getDate() + delta);
    return d.toLocaleDateString('en-CA');
}
function dayLabel(dateStr: string): string {
    return new Date(`${dateStr}T12:00:00`).toLocaleDateString([], {
        weekday: 'long', month: 'long', day: 'numeric',
    });
}

export function ScheduleRoute() {
    const [dateStr, setDateStr] = useState<string>(localToday());
    const [view, setView] = useState<'flow' | 'grid' | 'patients' | 'staff'>('flow');
    const { rooms, cases, stays } = useAscSchedule(dateStr);
    const staffing = useAscStaffing(dateStr, view === 'staff');
    const {
        advance, cancelCase, rescheduleCase, completeTurnover, releaseRoom,
        saveStaffAssignment, removeStaffAssignment, findSlots, bookCase,
    } = useAscActions(dateStr);
    const [editing, setEditing] = useState<OrCase | null>(null);
    const [cancelling, setCancelling] = useState<OrCase | null>(null);
    const [booking, setBooking] = useState(false);
    const [err, setErr] = useState<string>('');

    const isToday = dateStr === localToday();
    const loading = rooms.isLoading || cases.isLoading;
    const loadErr = rooms.error ?? cases.error ?? stays.error
        ?? (view === 'staff' ? staffing.staff.error ?? staffing.assignments.error : null);

    async function handleAdvance(c: OrCase, toStage: Stage, toRoomResourceId?: string) {
        setErr('');
        try {
            await advance.mutateAsync({ c, toStage, toRoomResourceId, stays: stays.data ?? [] });
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'Could not advance the case');
        }
    }

    async function handleMarkReady(roomResourceId: string) {
        setErr('');
        try {
            await completeTurnover.mutateAsync(roomResourceId);
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'Could not complete turnover');
        }
    }

    async function handleRelease(c: OrCase, stay: RoomStay) {
        setErr('');
        try {
            await releaseRoom.mutateAsync({ stay, reason: `manual_schedule_release: case ${c.case_id}` });
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'Could not release the assigned resource');
        }
    }

    const activeCases = (cases.data ?? []).filter((c) => (c.case_status ?? '') !== 'cancelled' && (c.schedule_status ?? '') !== 'cancelled');
    const activeStays = (stays.data ?? []).filter((s) => s.status === 'occupied').length;
    const turnoverRooms = (stays.data ?? []).filter((s) => s.status === 'cleaning').length;
    const staffedCount = new Set((staffing.assignments.data ?? []).map((a) => a.resource_id)).size;
    const tabs = [
        { id: 'flow' as const, label: 'Patient movement', icon: <HeartPulse size={14} /> },
        { id: 'grid' as const, label: 'Master schedule', icon: <LayoutGrid size={14} /> },
        { id: 'patients' as const, label: 'Patient roster', icon: <ListFilter size={14} /> },
        { id: 'staff' as const, label: 'Staff rosters', icon: <Users size={14} /> },
    ];

    return (
        <div className="min-h-full bg-pt-darker">
            <header className="border-b border-pt-border/60 px-6 py-4 flex items-center gap-4 flex-wrap">
                <Link to="/" className="text-pt-text-muted hover:text-pt-text flex items-center gap-2 text-sm">
                    <ArrowLeft size={18} /> Dashboard
                </Link>
                <h1 className="text-lg font-display font-semibold text-pt-text">Scheduling</h1>
                <div className="ml-auto flex items-center gap-2 flex-wrap">
                    <button type="button" aria-label="Previous day" onClick={() => setDateStr(shiftDay(dateStr, -1))}
                        className="rounded border border-pt-input-border p-1.5 text-pt-text-muted hover:text-pt-text hover:border-pt-cyan/40 transition-colors">
                        <ChevronLeft size={14} />
                    </button>
                    <span className="text-sm text-pt-text min-w-[168px] text-center">{dayLabel(dateStr)}</span>
                    <button type="button" aria-label="Next day" onClick={() => setDateStr(shiftDay(dateStr, 1))}
                        className="rounded border border-pt-input-border p-1.5 text-pt-text-muted hover:text-pt-text hover:border-pt-cyan/40 transition-colors">
                        <ChevronRight size={14} />
                    </button>
                    {!isToday && (
                        <button type="button" onClick={() => setDateStr(localToday())}
                            className="text-xs text-pt-cyan border border-pt-cyan/40 rounded px-2.5 py-1.5 hover:bg-pt-cyan/10 transition-colors">Today</button>
                    )}
                    <button type="button" onClick={() => setBooking(true)}
                        className="text-xs font-medium rounded bg-pt-gold text-pt-darker px-3 py-1.5 hover:opacity-90 transition-opacity ml-1">
                        + Book case
                    </button>
                    <span className="text-[11px] tracking-[0.08em] text-pt-cyan border border-pt-cyan/40 rounded px-2 py-0.5 ml-2">LIVE</span>
                </div>
            </header>

            <div className="p-6 space-y-6">
                {err && <div className="rounded border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-2.5">{err}</div>}
                {loadErr != null && (
                    <div className="rounded border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-2.5">
                        {loadErr instanceof Error ? loadErr.message : 'Failed to load schedule'}
                    </div>
                )}
                {loading && <div className="text-sm text-pt-text-muted">Loading schedule…</div>}

                {!loading && loadErr == null && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            {[
                                { label: 'Cases', value: activeCases.length, detail: 'scheduled today', tone: 'text-pt-gold' },
                                { label: 'Patients on floor', value: activeStays, detail: 'occupied resources', tone: 'text-pt-cyan' },
                                { label: 'Turnover', value: turnoverRooms, detail: 'awaiting ready', tone: turnoverRooms ? 'text-amber-400' : 'text-emerald-400' },
                                { label: 'Staffed areas', value: view === 'staff' ? staffedCount : '—', detail: view === 'staff' ? 'OR / PACU coverage' : 'open staff rosters', tone: 'text-emerald-400' },
                            ].map((m) => (
                                <div key={m.label} className="rounded-lg border border-pt-border bg-pt-panel px-4 py-3">
                                    <div className="text-[10px] uppercase tracking-[0.1em] text-pt-text-dim">{m.label}</div>
                                    <div className={`text-2xl font-display font-semibold ${m.tone}`}>{m.value}</div>
                                    <div className="text-[10px] text-pt-text-muted">{m.detail}</div>
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center gap-1 rounded-xl border border-pt-border bg-pt-panel p-1 overflow-x-auto">
                            {tabs.map((tab) => (
                                <button key={tab.id} type="button" onClick={() => setView(tab.id)}
                                    className={['shrink-0 flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs transition-colors', view === tab.id ? 'bg-pt-cyan/15 text-pt-cyan border border-pt-cyan/30' : 'text-pt-text-muted border border-transparent hover:text-pt-text hover:bg-white/[0.03]'].join(' ')}>
                                    {tab.icon}{tab.label}
                                </button>
                            ))}
                            <span className="ml-auto pr-2 hidden md:flex items-center gap-1 text-[10px] text-pt-text-dim"><CalendarDays size={12} /> Live data refreshes every 20 seconds</span>
                        </div>

                        {view === 'flow' && isToday && (
                            <FlowBoard
                                cases={cases.data ?? []}
                                rooms={rooms.data ?? []}
                                stays={stays.data ?? []}
                                onAdvance={handleAdvance}
                                onBlocked={setErr}
                                advancing={advance.isPending}
                                onMarkReady={handleMarkReady}
                                markingReady={completeTurnover.isPending}
                            />
                        )}
                        {view === 'flow' && !isToday && (
                            <div className="rounded-lg border border-pt-border bg-pt-panel px-5 py-8 text-center text-sm text-pt-text-muted">Patient movement is a live, same-day control surface. Use Master schedule or Patient roster for future dates.</div>
                        )}
                        {view === 'grid' && <>
                            <DayBriefPanel dateStr={dateStr} facilityId={ASC_FACILITY_ID} />
                            <OrGrid
                                cases={cases.data ?? []}
                                rooms={rooms.data ?? []}
                                onEdit={(c) => setEditing(c)}
                                onCancel={(c) => setCancelling(c)}
                            />
                        </>}
                        {view === 'patients' && <PatientScheduleRoster
                            cases={cases.data ?? []} rooms={rooms.data ?? []} stays={stays.data ?? []}
                            onAdvance={handleAdvance} onRelease={handleRelease}
                            advancing={advance.isPending} releasing={releaseRoom.isPending}
                        />}
                        {view === 'staff' && <StaffRosters
                            dateStr={dateStr} cases={cases.data ?? []} rooms={rooms.data ?? []}
                            staff={staffing.staff.data ?? []} assignments={staffing.assignments.data ?? []}
                            loading={staffing.staff.isLoading || staffing.assignments.isLoading}
                            saving={saveStaffAssignment.isPending} removing={removeStaffAssignment.isPending}
                            onSave={async (input) => {
                                setErr('');
                                try { await saveStaffAssignment.mutateAsync(input); }
                                catch (e) { setErr(e instanceof Error ? e.message : 'Could not assign clinical coverage'); }
                            }}
                            onRemove={async (id) => {
                                setErr('');
                                try { await removeStaffAssignment.mutateAsync(id); }
                                catch (e) { setErr(e instanceof Error ? e.message : 'Could not remove clinical coverage'); }
                            }}
                        />}
                    </>
                )}
            </div>

            {booking && (
                <BookCaseDialog
                    dateStr={dateStr}
                    rooms={rooms.data ?? []}
                    busy={bookCase.isPending}
                    findingSlots={findSlots.isPending}
                    error={err}
                    onClose={() => { setBooking(false); setErr(''); }}
                    onFindSlots={async (input) => {
                        setErr('');
                        try {
                            return await findSlots.mutateAsync(input);
                        } catch (e) {
                            setErr(e instanceof Error ? e.message : 'Could not load open slots');
                            return [];
                        }
                    }}
                    onBook={async (input) => {
                        setErr('');
                        try {
                            await bookCase.mutateAsync(input);
                            setBooking(false);
                        } catch (e) {
                            setErr(e instanceof Error ? e.message : 'Could not book the case');
                        }
                    }}
                />
            )}
            {editing && (
                <EditDialog
                    c={editing}
                    busy={rescheduleCase.isPending}
                    onClose={() => setEditing(null)}
                    onSave={async (newStartLocal, durationMins) => {
                        setErr('');
                        try {
                            await rescheduleCase.mutateAsync({ c: editing, newStartLocal, durationMins });
                            setEditing(null);
                        } catch (e) {
                            setErr(e instanceof Error ? e.message : 'Could not reschedule the case');
                        }
                    }}
                />
            )}
            {cancelling && (
                <CancelDialog
                    c={cancelling}
                    busy={cancelCase.isPending}
                    onClose={() => setCancelling(null)}
                    onConfirm={async (reason) => {
                        setErr('');
                        try {
                            await cancelCase.mutateAsync({ c: cancelling, reason });
                            setCancelling(null);
                        } catch (e) {
                            setErr(e instanceof Error ? e.message : 'Could not cancel the case');
                        }
                    }}
                />
            )}
        </div>
    );
}

function toLocalInputValue(ts: string | null): string {
    const d = ts ? new Date(ts) : new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EditDialog({ c, busy, onClose, onSave }: {
    c: OrCase; busy: boolean; onClose: () => void;
    onSave: (newStartLocal: string, durationMins: number) => void;
}) {
    const [start, setStart] = useState(toLocalInputValue(c.scheduled_start));
    const [duration, setDuration] = useState(c.estimated_duration_minutes ?? 60);
    return (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-6">
            <div className="w-full max-w-md rounded-lg border border-pt-border bg-pt-panel p-5">
                <h2 className="text-base font-display font-semibold text-pt-text">Edit case</h2>
                <p className="text-xs text-pt-text-muted mt-0.5 mb-4">{c.patient_name} · {c.procedure_name}</p>
                <label className="block text-xs text-pt-text-muted mb-1">Start</label>
                <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)}
                    className="w-full mb-3 rounded border border-pt-input-border bg-pt-darker text-pt-text text-sm px-3 py-2" />
                <div className="grid grid-cols-2 gap-3 mb-2">
                    <div>
                        <label className="block text-xs text-pt-text-muted mb-1">Protected room</label>
                        <div className="w-full rounded border border-pt-input-border bg-pt-darker/70 text-pt-text-muted text-sm px-3 py-2">{c.or_room ?? 'Room pending'}</div>
                    </div>
                    <div>
                        <label className="block text-xs text-pt-text-muted mb-1">Duration (min)</label>
                        <input type="number" min={15} step={15} value={duration}
                            onChange={(e) => setDuration(Math.max(15, Number(e.target.value) || 60))}
                            className="w-full rounded border border-pt-input-border bg-pt-darker text-pt-text text-sm px-3 py-2" />
                    </div>
                </div>
                <p className="text-[10px] text-pt-text-dim mb-4">Room changes are not edited as free text. This keeps the case row synchronized with the room participant reserved by the scheduling engine.</p>
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="text-sm text-pt-text-muted px-3 py-1.5 hover:text-pt-text">Close</button>
                    <button type="button" disabled={busy} onClick={() => onSave(start, duration)}
                        className="text-sm font-medium rounded bg-pt-gold text-pt-darker px-4 py-1.5 hover:opacity-90 disabled:opacity-50">
                        {busy ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CancelDialog({ c, busy, onClose, onConfirm }: {
    c: OrCase; busy: boolean; onClose: () => void; onConfirm: (reason: string) => void;
}) {
    const [reason, setReason] = useState('');
    const [touched, setTouched] = useState(false);
    return (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-6">
            <div className="w-full max-w-md rounded-lg border border-red-500/40 bg-pt-panel p-5">
                <h2 className="text-base font-display font-semibold text-pt-text">Cancel case</h2>
                <p className="text-xs text-pt-text-muted mt-0.5 mb-4">{c.patient_name} · {c.procedure_name} · {c.or_room}</p>
                <label className="block text-xs text-pt-text-muted mb-1">Reason (required)</label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                    className="w-full rounded border border-pt-input-border bg-pt-darker text-pt-text text-sm px-3 py-2" />
                {touched && !reason.trim() && <p className="text-xs text-red-400 mt-1">Enter a cancellation reason</p>}
                <div className="flex justify-end gap-2 mt-4">
                    <button type="button" onClick={onClose} className="text-sm text-pt-text-muted px-3 py-1.5 hover:text-pt-text">Keep case</button>
                    <button type="button" disabled={busy}
                        onClick={() => { if (!reason.trim()) { setTouched(true); return; } onConfirm(reason.trim()); }}
                        className="text-sm font-medium rounded bg-red-500/90 text-white px-4 py-1.5 hover:bg-red-500 disabled:opacity-50">
                        {busy ? 'Cancelling…' : 'Cancel case'}
                    </button>
                </div>
            </div>
        </div>
    );
}
